import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { patchJSON, postJSON } from '@/lib/api'
import {
  customFieldConversionPreview,
  customFieldNarrowingWarning,
  nonConversionPreviewError,
} from '@/lib/contacts/errors'
import { customFieldSetupPayload, customFieldUpdatePayload } from '@/lib/contacts/payloads'
import type {
  CustomField,
  CustomFieldResponse,
  CustomFieldSetupForm,
  CustomFieldType,
} from '@/lib/contacts/types'
import type {
  ConversionConfirmation,
  CustomFieldDialogsProps,
  NarrowingConfirmation,
} from '@/pages/CustomFieldDialogs'

export function useCustomFieldDialogState() {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editingField, setEditingField] = React.useState<CustomField | null>(null)
  const [choicesField, setChoicesField] = React.useState<CustomField | null>(null)
  const [conversion, setConversion] = React.useState<ConversionConfirmation | null>(null)
  const [narrowing, setNarrowing] = React.useState<NarrowingConfirmation | null>(null)
  const create = useCreateCustomField(() => setDialogOpen(false))
  const update = useUpdateCustomField(() => {
    setEditingField(null)
    setNarrowing(null)
  })
  const choices = useUpdateChoices(() => setChoicesField(null))
  const convert = useConvertCustomFieldType(() => setConversion(null))

  return {
    dialogProps: dialogProps({
      choices,
      choicesField,
      conversion,
      convert,
      create,
      dialogOpen,
      editingField,
      narrowing,
      setChoicesField,
      setConversion,
      setDialogOpen,
      setEditingField,
      setNarrowing,
      update,
    }),
    openCreate: () => setDialogOpen(true),
    openEdit: (field: CustomField) => {
      update.reset()
      setEditingField(field)
    },
  }
}

function dialogProps({
  choices,
  choicesField,
  conversion,
  convert,
  create,
  dialogOpen,
  editingField,
  narrowing,
  setChoicesField,
  setConversion,
  setDialogOpen,
  setEditingField,
  setNarrowing,
  update,
}: DialogPropsDependencies): CustomFieldDialogsProps {
  return {
    choicesError: choices.error,
    choicesField,
    choicesPending: choices.isPending,
    conversion,
    conversionError: nonConversionPreviewError(convert.error),
    conversionPending: convert.isPending,
    createError: create.error,
    createPending: create.isPending,
    dialogOpen,
    editingField,
    narrowingConfirmation: narrowing,
    onCancelConversion: () => closeConversion(convert, setConversion),
    onCancelNarrowing: () => cancelNarrowing(update, setNarrowing),
    onCloseChoices: () => closeChoices(choices, setChoicesField),
    onCloseCreate: () => closeCreate(create, setDialogOpen),
    onCloseEdit: () => closeEdit(update, setEditingField),
    onConfirmConversion: (field, fieldType) =>
      convert.mutate({ field, fieldType, confirmConversion: true }),
    onConfirmNarrowing: () => confirmNarrowing(narrowing, update),
    onConvertType: (field) => openConversion(field, convert, setEditingField, setConversion),
    onManageChoices: (field) => openChoices(field, choices, setEditingField, setChoicesField),
    onPreviewConversion: (field, fieldType) =>
      previewConversion(field, fieldType, convert, setConversion),
    onSubmitChoices: (field, fieldChoices) => choices.mutate({ field, choices: fieldChoices }),
    onSubmitCreate: (form) => create.mutate(form),
    onSubmitUpdate: (field, form) =>
      submitUpdate(field, form, update, setEditingField, setNarrowing),
    updateError: update.error,
    updatePending: update.isPending,
  }
}

function useCreateCustomField(onCreated: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (form: CustomFieldSetupForm) =>
      postJSON('/api/v1/custom_fields', { custom_field: customFieldSetupPayload(form) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      onCreated()
    },
  })
}

function useUpdateCustomField(onUpdated: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: UpdateCustomFieldParams) =>
      patchJSON<CustomFieldResponse>(`/api/v1/custom_fields/${params.field.id}`, {
        custom_field: customFieldUpdatePayload(params.form),
        ...(params.confirmNarrowing ? { confirm_narrowing: true } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      onUpdated()
    },
  })
}

function useUpdateChoices(onUpdated: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ field, choices }: { field: CustomField; choices: string[] }) =>
      patchJSON<CustomFieldResponse>(`/api/v1/custom_fields/${field.id}`, {
        custom_field: { choices },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      onUpdated()
    },
  })
}

function useConvertCustomFieldType(onConverted: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ConvertCustomFieldTypeParams) =>
      patchJSON<CustomFieldResponse>(`/api/v1/custom_fields/${params.field.id}`, {
        custom_field: { field_type: params.fieldType },
        ...(params.confirmConversion ? { confirm_conversion: true } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-fields'] })
      onConverted()
    },
  })
}

function closeCreate(create: MutationController, setDialogOpen: (open: boolean) => void) {
  create.reset()
  setDialogOpen(false)
}

function closeEdit(
  update: MutationController,
  setEditingField: (field: CustomField | null) => void,
) {
  update.reset()
  setEditingField(null)
}

function openChoices(
  field: CustomField,
  choices: MutationController,
  setEditingField: (field: CustomField | null) => void,
  setChoicesField: (field: CustomField | null) => void,
) {
  choices.reset()
  setEditingField(null)
  setChoicesField(field)
}

function closeChoices(
  choices: MutationController,
  setChoicesField: (field: CustomField | null) => void,
) {
  choices.reset()
  setChoicesField(null)
}

function openConversion(
  field: CustomField,
  convert: MutationController,
  setEditingField: (field: CustomField | null) => void,
  setConversion: (conversion: ConversionConfirmation | null) => void,
) {
  convert.reset()
  setEditingField(null)
  setConversion({ field, preview: null })
}

function closeConversion(
  convert: MutationController,
  setConversion: (conversion: ConversionConfirmation | null) => void,
) {
  convert.reset()
  setConversion(null)
}

function submitUpdate(
  field: CustomField,
  form: CustomFieldSetupForm,
  update: ReturnType<typeof useUpdateCustomField>,
  setEditingField: (field: CustomField | null) => void,
  setNarrowing: (narrowing: NarrowingConfirmation | null) => void,
) {
  update.mutate(
    { field, form },
    {
      onError: (error) => {
        const warning = customFieldNarrowingWarning(error)
        if (!warning) return

        update.reset()
        setEditingField(null)
        setNarrowing({ field, form, warning })
      },
    },
  )
}

function previewConversion(
  field: CustomField,
  fieldType: CustomFieldType,
  convert: ReturnType<typeof useConvertCustomFieldType>,
  setConversion: (conversion: ConversionConfirmation | null) => void,
) {
  convert.mutate(
    { field, fieldType },
    {
      onError: (error) => {
        const preview = customFieldConversionPreview(error)
        if (!preview) return

        convert.reset()
        setConversion({ field, preview })
      },
    },
  )
}

function cancelNarrowing(
  update: MutationController,
  setNarrowing: (narrowing: NarrowingConfirmation | null) => void,
) {
  update.reset()
  setNarrowing(null)
}

function confirmNarrowing(
  narrowing: NarrowingConfirmation | null,
  update: ReturnType<typeof useUpdateCustomField>,
) {
  if (!narrowing) return

  update.mutate({
    field: narrowing.field,
    form: narrowing.form,
    confirmNarrowing: true,
  })
}

type DialogPropsDependencies = {
  choices: ReturnType<typeof useUpdateChoices>
  choicesField: CustomField | null
  conversion: ConversionConfirmation | null
  convert: ReturnType<typeof useConvertCustomFieldType>
  create: ReturnType<typeof useCreateCustomField>
  dialogOpen: boolean
  editingField: CustomField | null
  narrowing: NarrowingConfirmation | null
  setChoicesField: (field: CustomField | null) => void
  setConversion: (conversion: ConversionConfirmation | null) => void
  setDialogOpen: (open: boolean) => void
  setEditingField: (field: CustomField | null) => void
  setNarrowing: (narrowing: NarrowingConfirmation | null) => void
  update: ReturnType<typeof useUpdateCustomField>
}

type UpdateCustomFieldParams = {
  field: CustomField
  form: CustomFieldSetupForm
  confirmNarrowing?: boolean
}

type ConvertCustomFieldTypeParams = {
  field: CustomField
  fieldType: CustomFieldType
  confirmConversion?: boolean
}

type MutationController = {
  reset: () => void
}
